import React, { useState, useEffect } from 'react';
import { config } from '../config';
import Spinner from '../components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import SearchableSelect from '../components/SearchableSelect';
import TagSelector from '../components/TagSelector';
import './Repairs.css';

function EditRepair({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [repair, setRepair] = useState(null);
  const [cars, setCars] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [hourlyRates, setHourlyRates] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showCreateInventoryModal, setShowCreateInventoryModal] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [withdrawQuantity, setWithdrawQuantity] = useState(1);
  const [createWithdrawQuantity, setCreateWithdrawQuantity] = useState(1);
  const [inventoryFormData, setInventoryFormData] = useState({
    name: '',
    description: '',
    category: '',
    quantity: 1,
    unit: 'Stück',
    locationId: '',
    purchasePrice: 0,
    sellingPrice: 0,
    currentValue: 0,
    partNumber: ''
  });
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [templateFormData, setTemplateFormData] = useState({ name: '', description: '' });
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [formData, setFormData] = useState({
    carId: '',
    description: '',
    status: 'pending',
    date: new Date().toISOString().split('T')[0],
    mileage: '',
    materials: [{ description: '', amount: 0, quantity: 1 }],
    labor: [],
    serviceTemplateIds: [],
    assignedUserId: '',
    internalNotes: [],
    tags: [],
    materialsDiscount: '',
    laborDiscount: '',
    freeLabor: [],
    materialsDiscountType: 'percent',
    laborDiscountType: 'percent'
  });

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId, id]);

  useEffect(() => {
    if (inventoryFormData.quantity && createWithdrawQuantity > inventoryFormData.quantity) {
      setCreateWithdrawQuantity(Math.max(1, inventoryFormData.quantity));
    }
  }, [inventoryFormData.quantity]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadCars(),
        loadCustomers(),
        loadServiceTemplates(),
        loadHourlyRates(),
        loadInventory(),
        loadLocations(),
        loadUsers(),
        loadRoles(),
        loadTags()
      ]);
      await loadRepair();
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const getCurrency = () => {
    return getCurrencySymbol(tenant?.taxSystem || 'DE');
  };

  const loadRepair = async () => {
    try {
      const data = await api.get(`/repairs/${id}`, tenantId);
      setRepair(data);
      const car = cars.find(c => c.id === data.carId);
      
      let materials = data.materials || [];
      let labor = data.labor || [];
      
      if (data.items && Array.isArray(data.items) && data.items.length > 0 && (!materials.length && !labor.length)) {
        materials = data.items.filter(item => !item.hours && !item.hourlyRateId).map(item => ({
          description: item.description || '',
          amount: item.amount || 0,
          quantity: item.quantity || 1
        }));
        labor = data.items.filter(item => item.hours || item.hourlyRateId).map(item => ({
          hours: item.hours === 0 || item.hours === '0' ? '' : (item.hours || ''),
          hourlyRateId: item.hourlyRateId || '',
          description: item.description || '',
          date: item.date || '',
          documentation: item.documentation || '',
          mileage: item.mileage || ''
        }));
      }
      
      if (materials.length === 0) {
        materials = [{ description: '', amount: 0, quantity: 1 }];
      }
      labor = labor.map(lab => ({
        ...lab,
        hours: lab.hours === 0 || lab.hours === '0' ? '' : (lab.hours || ''),
        hourlyRateId: lab.hourlyRateId || '',
        date: lab.date || '',
        documentation: lab.documentation || '',
        mileage: lab.mileage || ''
      }));
      
      const selectedCar = cars.find(c => c.id === data.carId);
      if (selectedCar && selectedCar.customerId) {
        setSelectedCustomerId(selectedCar.customerId);
      }
      
      setFormData({
        carId: data.carId,
        description: data.description || '',
        internalNotes: data.internalNotes || [],
        status: data.status || 'pending',
        date: data.date ? data.date.split('T')[0] : new Date().toISOString().split('T')[0],
        mileage: data.mileage || (car ? car.mileage : ''),
        materials,
        labor: labor.map(lab => ({
          ...lab,
          id: lab.id || `${lab.description || ''}-${lab.hours || ''}-${lab.hourlyRateId || ''}`
        })),
        serviceTemplateIds: data.serviceTemplateIds || [],
        assignedUserId: data.assignedUserId || '',
        tags: data.tags || [],
        materialsDiscount: data.materialsDiscount || '',
        laborDiscount: data.laborDiscount || '',
        freeLabor: data.freeLabor || [],
        materialsDiscountType: typeof data.materialsDiscount === 'number' && data.materialsDiscount > 100 ? 'amount' : 'percent',
        laborDiscountType: typeof data.laborDiscount === 'number' && data.laborDiscount > 100 ? 'amount' : 'percent'
      });
      setSelectedTags(data.tags || []);
    } catch (error) {
      console.error('Error loading repair:', error);
      showNotification('Fehler beim Laden der Reparatur', 'error');
      navigate('/repairs');
    }
  };

  const loadCars = async () => {
    try {
      const data = await api.get('/cars', tenantId);
      setCars(data);
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await api.get('/customers', tenantId);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadServiceTemplates = async () => {
    try {
      const data = await api.get('/service-templates', tenantId);
      setServiceTemplates(data);
    } catch (error) {
      console.error('Error loading service templates:', error);
    }
  };

  const loadHourlyRates = async () => {
    try {
      const data = await api.get('/hourly-rates', tenantId);
      setHourlyRates(data);
    } catch (error) {
      console.error('Error loading hourly rates:', error);
    }
  };

  const loadInventory = async () => {
    try {
      const data = await api.get('/inventory', tenantId);
      setInventory(data.filter(item => (item.quantity || 0) > 0));
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await api.get('/locations', tenantId);
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const tenantUsers = data.filter(u => u.tenantId === tenantId);
        setUsers(tenantUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await api.get('/roles', tenantId);
      setRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoles([]);
    }
  };

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };

  const handleTemplateToggle = async (templateId) => {
    const currentIds = formData.serviceTemplateIds || [];
    const isSelected = currentIds.includes(templateId);
    
    let newIds;
    if (isSelected) {
      newIds = currentIds.filter(id => id !== templateId);
    } else {
      newIds = [...currentIds, templateId];
    }
    
    // Sammle alle Materialien und Arbeitszeiten aus allen ausgewählten Templates
    let templateMaterials = [];
    let templateLabor = [];
    
    // Wenn Template hinzugefügt wird, prüfe ob Materialien aus Inventar entnommen werden sollen
    if (!isSelected) {
      const template = serviceTemplates.find(t => t.id === templateId);
      if (template && template.materials && Array.isArray(template.materials)) {
        for (const mat of template.materials) {
          if (mat.inventoryId) {
            // Material aus Inventar entnehmen
            try {
              const item = inventory.find(i => i.id === mat.inventoryId);
              if (item && item.quantity >= (mat.quantity || 1)) {
                const response = await api.post(`/inventory/${mat.inventoryId}/withdraw`, { 
                  quantity: mat.quantity || 1 
                }, tenantId);
                templateMaterials.push({
                  description: item.name,
                  amount: response.sellingPrice || mat.amount || 0,
                  quantity: mat.quantity || 1,
                  inventoryId: mat.inventoryId
                });
                await loadInventory();
              } else {
                // Nicht genug im Inventar, verwende Template-Werte
                templateMaterials.push({ ...mat });
              }
            } catch (error) {
              console.error('Error withdrawing from inventory:', error);
              // Bei Fehler verwende Template-Werte
              templateMaterials.push({ ...mat });
            }
          } else {
            templateMaterials.push({ ...mat });
          }
        }
      }
    } else {
      // Template wird entfernt, sammle Materialien aus verbleibenden Templates
      newIds.forEach(id => {
        const template = serviceTemplates.find(t => t.id === id);
        if (template && template.materials && Array.isArray(template.materials)) {
          templateMaterials = [...templateMaterials, ...template.materials.map(m => ({ ...m }))];
        }
      });
    }
    
    // Sammle Arbeitszeiten aus allen ausgewählten Templates
    const selectedCar = cars.find(c => c.id === formData.carId);
    const carMileage = selectedCar?.mileage ? String(selectedCar.mileage) : '';
    const today = new Date().toISOString().split('T')[0];
    
    newIds.forEach(id => {
      const template = serviceTemplates.find(t => t.id === id);
      if (template && template.labor && Array.isArray(template.labor)) {
        templateLabor = [...templateLabor, ...template.labor.map(l => ({
          ...l,
          date: l.date || today,
          mileage: l.mileage || carMileage
        }))];
      }
    });
    
    // Behalte nur manuell hinzugefügte Materialien und Arbeitszeiten
    // (diejenigen, die nicht aus Templates stammen)
    const manualMaterials = formData.materials.filter(mat => {
      // Prüfe, ob dieses Material aus einem Template stammt
      return !newIds.some(id => {
        const template = serviceTemplates.find(t => t.id === id);
        if (!template || !template.materials) return false;
        return template.materials.some(templateMat => 
          templateMat.description === mat.description && 
          templateMat.amount === mat.amount &&
          templateMat.quantity === mat.quantity
        );
      });
    });
    
    const manualLabor = formData.labor.filter(lab => {
      // Prüfe, ob diese Arbeitszeit aus einem Template stammt
      return !newIds.some(id => {
        const template = serviceTemplates.find(t => t.id === id);
        if (!template || !template.labor) return false;
        return template.labor.some(templateLab => 
          templateLab.hours === lab.hours &&
          templateLab.hourlyRateId === lab.hourlyRateId &&
          templateLab.description === lab.description
        );
      });
    });
    
    // Kombiniere Template-Materialien/Arbeitszeiten mit manuellen
    let newMaterials = [...templateMaterials, ...manualMaterials];
    let newLabor = [...templateLabor, ...manualLabor];
    
    // Filtere leere Einträge
    newMaterials = newMaterials.filter(mat => 
      mat.description && mat.description.trim() !== '' && 
      (mat.amount > 0 || mat.quantity > 0)
    );
    
    newLabor = newLabor.filter(lab => 
      (lab.hours && parseFloat(lab.hours) > 0) || lab.description
    );
    
    // Stelle sicher, dass mindestens ein leerer Eintrag vorhanden ist
    if (newMaterials.length === 0) {
      newMaterials = [{ description: '', amount: 0, quantity: 1 }];
    }
    if (newLabor.length === 0) {
      const selectedCar = cars.find(c => c.id === formData.carId);
      const carMileage = selectedCar?.mileage || '';
      const today = new Date().toISOString().split('T')[0];
      const defaultRate = hourlyRates.find(r => r.isDefault);
      newLabor = [{ 
        hours: '', 
        hourlyRateId: defaultRate?.id || '', 
        description: '', 
        date: today, 
        documentation: '', 
        mileage: carMileage ? String(carMileage) : '' 
      }];
    }
    
    setFormData({
      ...formData,
      serviceTemplateIds: newIds,
      materials: newMaterials,
      labor: newLabor
    });
  };

  const calculateLaborAmount = (lab) => {
    if (lab.hours && lab.hourlyRateId) {
      const rate = hourlyRates.find(r => r.id === lab.hourlyRateId);
      if (rate) {
        return lab.hours * rate.rate;
      }
    }
    return 0;
  };

  const isLaborFromTemplate = (lab) => {
    if (!formData.serviceTemplateIds || formData.serviceTemplateIds.length === 0) {
      return false;
    }
    return formData.serviceTemplateIds.some(templateId => {
      const template = serviceTemplates.find(t => t.id === templateId);
      if (!template || !template.labor) return false;
      return template.labor.some(templateLab => 
        templateLab.hours === lab.hours &&
        templateLab.hourlyRateId === lab.hourlyRateId &&
        templateLab.description === lab.description
      );
    });
  };

  const handleMaterialChange = (index, field, value) => {
    const newMaterials = [...formData.materials];
    if (field === 'amount' || field === 'quantity') {
      newMaterials[index] = { ...newMaterials[index], [field]: value === '' ? '' : value };
    } else {
      newMaterials[index] = { ...newMaterials[index], [field]: value };
    }
    setFormData({ ...formData, materials: newMaterials });
  };

  const handleMaterialBlur = (index, field) => {
    const newMaterials = [...formData.materials];
    if (field === 'amount' || field === 'quantity') {
      const val = newMaterials[index][field] === '' ? 0 : parseFloat(newMaterials[index][field]) || 0;
      newMaterials[index] = { ...newMaterials[index], [field]: val };
      setFormData({ ...formData, materials: newMaterials });
    }
  };

  const handleLaborChange = (index, field, value) => {
    const newLabor = [...formData.labor];
    if (field === 'hours') {
      newLabor[index] = { ...newLabor[index], [field]: value === '' ? '' : value };
    } else {
      newLabor[index] = { ...newLabor[index], [field]: value };
    }
    setFormData({ ...formData, labor: newLabor });
  };

  const handleLaborBlur = (index, field) => {
    const newLabor = [...formData.labor];
    if (field === 'hours') {
      const currentValue = newLabor[index][field];
      if (currentValue === '' || currentValue === '0' || currentValue === 0) {
        newLabor[index] = { ...newLabor[index], [field]: '' };
      } else {
        const val = parseFloat(currentValue) || '';
        newLabor[index] = { ...newLabor[index], [field]: val };
      }
      setFormData({ ...formData, labor: newLabor });
    }
  };

  const addMaterial = () => {
    const currentMaterials = formData.materials.filter(mat => 
      mat.description && mat.description.trim() !== ''
    );
    setFormData({
      ...formData,
      materials: [...currentMaterials, { description: '', amount: 0, quantity: 1 }]
    });
  };

  const removeMaterial = (index) => {
    const newMaterials = formData.materials.filter((_, i) => i !== index);
    if (newMaterials.length === 0) {
      newMaterials.push({ description: '', amount: 0, quantity: 1 });
    }
    setFormData({ ...formData, materials: newMaterials });
  };

  const addLabor = () => {
    const selectedCar = cars.find(c => c.id === formData.carId);
    const carMileage = selectedCar?.mileage || '';
    const today = new Date().toISOString().split('T')[0];
    const defaultRate = hourlyRates.find(r => r.isDefault);
    
    const newLabId = `lab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setFormData({
      ...formData,
      labor: [...formData.labor, { 
        id: newLabId,
        hours: '', 
        hourlyRateId: defaultRate?.id || '', 
        description: '', 
        date: today, 
        documentation: '', 
        mileage: carMileage ? String(carMileage) : '' 
      }]
    });
  };

  const removeLabor = (index) => {
    const newLabor = formData.labor.filter((_, i) => i !== index);
    if (newLabor.length === 0) {
      const selectedCar = cars.find(c => c.id === formData.carId);
      const carMileage = selectedCar?.mileage ? String(selectedCar.mileage) : '';
      const today = new Date().toISOString().split('T')[0];
      const defaultRate = hourlyRates.find(r => r.isDefault);
      newLabor.push({ 
        hours: '', 
        hourlyRateId: defaultRate?.id || '', 
        description: '', 
        date: today, 
        documentation: '', 
        mileage: carMileage
      });
    }
    setFormData({ ...formData, labor: newLabor });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        carId: formData.carId,
        description: formData.description || '',
        status: formData.status,
        date: formData.date,
        mileage: formData.mileage === '' ? null : parseInt(formData.mileage) || null,
        materials: formData.materials.map(mat => ({
          description: mat.description || '',
          amount: mat.amount === '' ? 0 : parseFloat(mat.amount) || 0,
          quantity: mat.quantity === '' ? 1 : parseFloat(mat.quantity) || 1
        })),
        labor: formData.labor.map(lab => ({
          hours: lab.hours === '' ? 0 : parseFloat(lab.hours) || 0,
          hourlyRateId: lab.hourlyRateId || '',
          description: lab.description || '',
          date: lab.date || '',
          documentation: lab.documentation || '',
          mileage: lab.mileage === '' ? null : parseInt(lab.mileage) || null,
          id: lab.id || `${lab.description || ''}-${lab.hours || ''}-${lab.hourlyRateId || ''}`
        })),
        assignedUserId: formData.assignedUserId || null,
        internalNotes: formData.internalNotes || [],
        tags: selectedTags,
        materialsDiscount: formData.materialsDiscount === '' ? null : (formData.materialsDiscountType === 'percent' ? parseFloat(formData.materialsDiscount) : parseFloat(formData.materialsDiscount)),
        laborDiscount: formData.laborDiscount === '' ? null : (formData.laborDiscountType === 'percent' ? parseFloat(formData.laborDiscount) : parseFloat(formData.laborDiscount)),
        freeLabor: formData.freeLabor || []
      };
      
      await api.put(`/repairs/${id}`, submitData, tenantId);
      navigate('/repairs');
    } catch (error) {
      console.error('Error saving repair:', error);
      showNotification('Fehler beim Speichern der Reparatur: ' + (error.response?.data?.error || error.message || 'Unbekannter Fehler', 'error'));
    }
  };

  const handleCreateTemplate = async () => {
    try {
      if (!templateFormData.name || templateFormData.name.trim() === '') {
        showNotification('Bitte geben Sie einen Namen für den Standardauftrag ein.', 'error');
        return;
      }

      const templateData = {
        name: templateFormData.name,
        description: templateFormData.description || formData.description || '',
        materials: formData.materials.filter(mat => mat.description && mat.description.trim() !== '').map(mat => ({
          description: mat.description,
          amount: mat.amount || 0,
          quantity: mat.quantity || 1
        })),
        labor: formData.labor.filter(lab => (lab.hours && lab.hours > 0) || (lab.description && lab.description.trim() !== '')).map(lab => ({
          hours: lab.hours || 0,
          hourlyRateId: lab.hourlyRateId || '',
          description: lab.description || ''
        }))
      };

      await api.post('/service-templates', templateData, tenantId);
      setShowCreateTemplateModal(false);
      setTemplateFormData({ name: '', description: '' });
    } catch (error) {
      console.error('Error creating template:', error);
      showNotification('Fehler beim Erstellen des Standardauftrags: ' + (error.response?.data?.error || error.message || 'Unbekannter Fehler', 'error'));
    }
  };

  let materialsAmount = formData.materials.reduce((sum, mat) => sum + ((mat.amount || 0) * (mat.quantity || 1)), 0);
  let laborAmount = formData.labor.reduce((sum, lab) => {
    const labId = lab.id || `${lab.description || ''}-${lab.hours || ''}-${lab.hourlyRateId || ''}`;
    const isFree = formData.freeLabor.includes(labId);
    return sum + (isFree ? 0 : calculateLaborAmount(lab));
  }, 0);
  
  if (formData.materialsDiscount) {
    const discount = formData.materialsDiscountType === 'percent' 
      ? (materialsAmount * (parseFloat(formData.materialsDiscount) / 100))
      : parseFloat(formData.materialsDiscount);
    materialsAmount = Math.max(0, materialsAmount - discount);
  }
  
  if (formData.laborDiscount) {
    const discount = formData.laborDiscountType === 'percent'
      ? (laborAmount * (parseFloat(formData.laborDiscount) / 100))
      : parseFloat(formData.laborDiscount);
    laborAmount = Math.max(0, laborAmount - discount);
  }
  
  const totalAmount = materialsAmount + laborAmount;

  if (loading) {
    return <Spinner />;
  }

  if (!repair) {
    return null;
  }

  return (
    <div className="repairs">
      <div className="page-header">
        <h1>Reparatur bearbeiten</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            type="button"
            onClick={() => setShowCreateTemplateModal(true)} 
            className="btn-secondary"
          >
            Als Standardauftrag speichern
          </button>
          <button type="button" onClick={() => navigate('/repairs')} className="btn-secondary">
            Zurück
          </button>
        </div>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Kunde (optional)</label>
            <SearchableSelect
              value={selectedCustomerId}
              onChange={(customerId) => {
                setSelectedCustomerId(customerId);
                // Wenn Kunde geändert wird und Auto nicht zu diesem Kunden gehört, Auto zurücksetzen
                if (customerId && formData.carId) {
                  const selectedCar = cars.find(c => c.id === formData.carId);
                  if (selectedCar && selectedCar.customerId !== customerId) {
                    setFormData({ ...formData, carId: '', mileage: '' });
                  }
                }
              }}
              options={customers}
              getOptionLabel={(customer) => `${customer.firstName} ${customer.lastName}`}
              getOptionValue={(customer) => customer.id}
              placeholder="Kunde auswählen..."
            />
          </div>
          <div className="form-group">
            <label>Auto *</label>
            <SearchableSelect
              value={formData.carId}
              onChange={(carId) => {
                const selectedCar = cars.find(c => c.id === carId);
                // Wenn Auto ausgewählt wird, automatisch Kunde setzen
                if (selectedCar && selectedCar.customerId) {
                  setSelectedCustomerId(selectedCar.customerId);
                }
                setFormData({ 
                  ...formData, 
                  carId: carId,
                  mileage: selectedCar ? (selectedCar.mileage || '') : formData.mileage
                });
              }}
              options={(() => {
                // Wenn Kunde ausgewählt, nur Autos dieses Kunden anzeigen
                if (selectedCustomerId) {
                  return cars.filter(car => car.customerId === selectedCustomerId);
                }
                return cars;
              })()}
              getOptionLabel={(car) => `${car.make} ${car.model} (${car.licensePlate})${car.mileage ? ` - ${car.mileage.toLocaleString('de-DE')} km` : ''}`}
              getOptionValue={(car) => car.id}
              placeholder="Auto auswählen..."
              required
            />
          </div>

          <div className="form-group">
            <label>Standardaufträge (optional)</label>
            <div className="template-buttons">
              {serviceTemplates.map(template => (
                <div
                  key={template.id}
                  className={`template-button ${formData.serviceTemplateIds?.includes(template.id) ? 'selected' : ''}`}
                  onClick={() => handleTemplateToggle(template.id)}
                >
                  {template.name}
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Beschreibung (optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Optionale Beschreibung der Reparatur"
            />
          </div>

          <div className="form-group">
            <label>Tags (optional)</label>
            <TagSelector
              tenantId={tenantId}
              selectedTags={selectedTags}
              onChange={setSelectedTags}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="pending">Ausstehend</option>
                <option value="in_progress">In Bearbeitung</option>
                <option value="completed">Abgeschlossen</option>
              </select>
            </div>
            <div className="form-group">
              <label>Zugewiesen an</label>
              <select
                value={formData.assignedUserId}
                onChange={(e) => setFormData({ ...formData, assignedUserId: e.target.value })}
              >
                <option value="">Keine Zuweisung</option>
                {users.map(user => {
                  const role = roles.find(r => r.id === user.roleId);
                  const roleName = role ? role.name : (user.userRole || '');
                  return (
                    <option key={user.id} value={user.id}>
                      {user.username} {roleName ? `(${roleName})` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Datum</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Kilometerstand</label>
              <input
                type="number"
                value={formData.mileage}
                onChange={(e) => setFormData({ ...formData, mileage: e.target.value === '' ? '' : e.target.value })}
                onBlur={(e) => {
                  const val = e.target.value === '' ? null : parseInt(e.target.value) || null;
                  setFormData({ ...formData, mileage: val });
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Materialien</label>
            {formData.materials.map((material, index) => (
              <div key={index} className="item-row">
                <input
                  type="text"
                  placeholder="Beschreibung"
                  value={material.description}
                  onChange={(e) => handleMaterialChange(index, 'description', e.target.value)}
                  className="item-description"
                />
                <div className="item-quantity-wrapper">
                  <input
                    type="number"
                    placeholder="Menge"
                    value={material.quantity}
                    onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                    onBlur={() => handleMaterialBlur(index, 'quantity')}
                    className="item-quantity"
                    min="0"
                    step="0.01"
                  />
                  <span className="input-unit">Stück</span>
                </div>
                <div className="item-amount-wrapper">
                  <input
                    type="number"
                    placeholder="Betrag"
                    value={material.amount}
                    onChange={(e) => handleMaterialChange(index, 'amount', e.target.value)}
                    onBlur={() => handleMaterialBlur(index, 'amount')}
                    className="item-amount"
                    min="0"
                    step="0.01"
                  />
                  <span className="input-unit">{getCurrency()}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeMaterial(index)}
                  className="btn-small btn-danger"
                >
                  Entfernen
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" onClick={addMaterial} className="btn-secondary btn-small">
                Material hinzufügen
              </button>
              <button 
                type="button" 
                onClick={() => setShowInventoryModal(true)} 
                className="btn-secondary btn-small"
              >
                Aus Inventar entnehmen
              </button>
              <button 
                type="button" 
                onClick={() => setShowCreateInventoryModal(true)} 
                className="btn-secondary btn-small"
              >
                Neues Inventar erfassen
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Arbeitseinsatz</label>
            {formData.labor.map((lab, index) => (
              <div key={index} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                <div className="item-row-extended" style={{ marginBottom: '0.5rem' }}>
                  <div className="item-hours-wrapper">
                    <input
                      type="number"
                      placeholder="Stunden"
                      value={lab.hours === 0 || lab.hours === '0' || lab.hours === '' ? '' : lab.hours}
                      onChange={(e) => handleLaborChange(index, 'hours', e.target.value)}
                      onBlur={() => handleLaborBlur(index, 'hours')}
                      className="item-hours"
                      min="0"
                      step="0.1"
                    />
                    <span className="input-unit">h</span>
                  </div>
                  <select
                    value={lab.hourlyRateId || ''}
                    onChange={(e) => handleLaborChange(index, 'hourlyRateId', e.target.value)}
                    className="item-hourly-rate"
                    style={{ flex: 1 }}
                  >
                    <option value="">Stundensatz wählen</option>
                    {hourlyRates.map(rate => (
                      <option key={rate.id} value={rate.id}>
                        {rate.name} ({rate.rate.toFixed(2)} {getCurrency()}/h)
                      </option>
                    ))}
                  </select>
                  {lab.hours && lab.hourlyRateId && (
                    <div className="item-amount-wrapper">
                      <span className="input-unit" style={{ position: 'static', padding: '0.5rem' }}>
                        {calculateLaborAmount(lab).toFixed(2)} {getCurrency()}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeLabor(index)}
                    className="btn-small btn-danger"
                  >
                    Entfernen
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Datum</label>
                    <input
                      type="date"
                      value={lab.date || ''}
                      onChange={(e) => handleLaborChange(index, 'date', e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', fontSize: '0.9rem' }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Kilometerstand (optional)</label>
                    <input
                      type="number"
                      value={lab.mileage || ''}
                      onChange={(e) => handleLaborChange(index, 'mileage', e.target.value)}
                      placeholder="km"
                      style={{ width: '100%', padding: '0.5rem', fontSize: '0.9rem' }}
                    />
                  </div>
                </div>
                {!isLaborFromTemplate(lab) && (
                  <div className="form-group" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                    <label style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Beschreibung (optional)</label>
                    <input
                      type="text"
                      value={lab.description || ''}
                      onChange={(e) => handleLaborChange(index, 'description', e.target.value)}
                      placeholder="Beschreibung der Arbeit..."
                      style={{ width: '100%', padding: '0.5rem', fontSize: '0.9rem' }}
                    />
                  </div>
                )}
                <div className="form-group" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.freeLabor.includes(lab.id || `${lab.description || ''}-${lab.hours || ''}-${lab.hourlyRateId || ''}`)}
                      onChange={(e) => {
                        const labId = lab.id || `${lab.description || ''}-${lab.hours || ''}-${lab.hourlyRateId || ''}`;
                        const newFreeLabor = e.target.checked
                          ? [...formData.freeLabor, labId]
                          : formData.freeLabor.filter(id => id !== labId);
                        setFormData({ ...formData, freeLabor: newFreeLabor });
                      }}
                    />
                    <span style={{ fontSize: '0.85rem' }}>Kostenlos (nicht berechnen)</span>
                  </label>
                </div>
                <div className="form-group" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                  <label style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Dokumentation</label>
                  <textarea
                    value={lab.documentation || ''}
                    onChange={(e) => handleLaborChange(index, 'documentation', e.target.value)}
                    placeholder="Kurze Dokumentation der Arbeit..."
                    rows="3"
                    style={{ width: '100%', padding: '0.5rem', fontSize: '0.9rem', resize: 'vertical' }}
                  />
                </div>
              </div>
            ))}
            <button type="button" onClick={addLabor} className="btn-secondary btn-small">
              Arbeitseinsatz hinzufügen
            </button>
          </div>

          <div className="form-group" style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Rabatte</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Rabatt auf Materialien</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={formData.materialsDiscount}
                    onChange={(e) => setFormData({ ...formData, materialsDiscount: e.target.value })}
                    placeholder="0"
                    style={{ flex: 1 }}
                    step="0.01"
                    min="0"
                  />
                  <select
                    value={formData.materialsDiscountType || 'percent'}
                    onChange={(e) => {
                      const type = e.target.value;
                      setFormData({ 
                        ...formData, 
                        materialsDiscountType: type,
                        materialsDiscount: '' 
                      });
                    }}
                    style={{ width: '80px' }}
                  >
                    <option value="percent">%</option>
                    <option value="amount">{getCurrency()}</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label>Rabatt auf Arbeitszeit</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={formData.laborDiscount}
                    onChange={(e) => setFormData({ ...formData, laborDiscount: e.target.value })}
                    placeholder="0"
                    style={{ flex: 1 }}
                    step="0.01"
                    min="0"
                  />
                  <select
                    value={formData.laborDiscountType || 'percent'}
                    onChange={(e) => {
                      const type = e.target.value;
                      setFormData({ 
                        ...formData, 
                        laborDiscountType: type,
                        laborDiscount: '' 
                      });
                    }}
                    style={{ width: '80px' }}
                  >
                    <option value="percent">%</option>
                    <option value="amount">{getCurrency()}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="total-amount">
            <strong>Gesamtbetrag: {totalAmount.toFixed(2)} {getCurrency()}</strong>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              Speichern
            </button>
            <button
              type="button"
              onClick={() => navigate('/repairs')}
              className="btn-secondary"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>

      {showInventoryModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Aus Inventar entnehmen</h2>
            <div className="form-group">
              <label>Inventar-Item</label>
              <select
                value={selectedInventoryItem || ''}
                onChange={(e) => {
                  const item = inventory.find(i => i.id === e.target.value);
                  setSelectedInventoryItem(item ? e.target.value : null);
                  if (item) {
                    setWithdrawQuantity(1);
                  }
                }}
              >
                <option value="">Bitte wählen</option>
                {inventory.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name} (Verfügbar: {item.quantity} {item.unit}, Verkaufspreis: {item.sellingPrice?.toFixed(2) || 0} {getCurrency()})
                  </option>
                ))}
              </select>
            </div>
            {selectedInventoryItem && (
              <>
                <div className="form-group">
                  <label>Menge</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={inventory.find(i => i.id === selectedInventoryItem)?.quantity || 0}
                    value={withdrawQuantity}
                    onChange={(e) => setWithdrawQuantity(parseFloat(e.target.value) || 0)}
                  />
                  <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                    {inventory.find(i => i.id === selectedInventoryItem)?.unit}
                  </span>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const item = inventory.find(i => i.id === selectedInventoryItem);
                        if (!item) return;
                        
                        const response = await api.post(`/inventory/${selectedInventoryItem}/withdraw`, { quantity: withdrawQuantity }, tenantId);
                        
                        const newMaterial = {
                          description: item.name,
                          amount: response.sellingPrice || 0,
                          quantity: withdrawQuantity
                        };
                        
                        const currentMaterials = formData.materials.filter(mat => 
                          mat.description && mat.description.trim() !== ''
                        );
                        
                        setFormData({
                          ...formData,
                          materials: [...currentMaterials, newMaterial]
                        });
                        
                        await loadInventory();
                        setShowInventoryModal(false);
                        setSelectedInventoryItem(null);
                        setWithdrawQuantity(1);
                      } catch (error) {
                        console.error('Error withdrawing from inventory:', error);
                        showNotification('Fehler beim Entnehmen aus Inventar: ' + (error.response?.data?.error || error.message || 'Unbekannter Fehler', 'error'));
                      }
                    }}
                    className="btn-primary"
                    disabled={!selectedInventoryItem || withdrawQuantity <= 0}
                  >
                    Entnehmen
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowInventoryModal(false);
                      setSelectedInventoryItem(null);
                      setWithdrawQuantity(1);
                    }}
                    className="btn-secondary"
                  >
                    Abbrechen
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showCreateTemplateModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Standardauftrag erstellen</h2>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={templateFormData.name}
                onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                placeholder="z.B. Ölwechsel"
                required
              />
            </div>
            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                value={templateFormData.description}
                onChange={(e) => setTemplateFormData({ ...templateFormData, description: e.target.value })}
                rows="3"
                placeholder={formData.description || 'Optionale Beschreibung'}
              />
            </div>
            <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                Die Materialien und Arbeitseinsätze aus dieser Reparatur werden übernommen.
              </p>
            </div>
            <div className="form-actions">
              <button
                type="button"
                onClick={handleCreateTemplate}
                className="btn-primary"
              >
                Erstellen
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateTemplateModal(false);
                  setTemplateFormData({ name: '', description: '' });
                }}
                className="btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateInventoryModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Neues Inventar erfassen</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const newItem = await api.post('/inventory', inventoryFormData, tenantId);
                
                // Automatisch aus Inventar entnehmen und zur Materialliste hinzufügen
                const withdrawQty = Math.min(createWithdrawQuantity, newItem.quantity || 0);
                if (withdrawQty > 0) {
                  try {
                    const response = await api.post(`/inventory/${newItem.id}/withdraw`, { 
                      quantity: withdrawQty 
                    }, tenantId);
                    
                    const newMaterial = {
                      description: newItem.name,
                      amount: response.sellingPrice || newItem.sellingPrice || 0,
                      quantity: withdrawQty,
                      inventoryId: newItem.id
                    };
                  
                  const currentMaterials = formData.materials.filter(mat => 
                    mat.description && mat.description.trim() !== ''
                  );
                  
                  setFormData({
                    ...formData,
                    materials: [...currentMaterials, newMaterial]
                  });
                  
                    await loadInventory();
                  } catch (withdrawError) {
                    console.error('Error withdrawing from inventory:', withdrawError);
                    // Auch ohne Entnahme hinzufügen
                    const newMaterial = {
                      description: newItem.name,
                      amount: newItem.sellingPrice || 0,
                      quantity: withdrawQty,
                      inventoryId: newItem.id
                    };
                  
                  const currentMaterials = formData.materials.filter(mat => 
                    mat.description && mat.description.trim() !== ''
                  );
                  
                    setFormData({
                      ...formData,
                      materials: [...currentMaterials, newMaterial]
                    });
                  }
                }
                
                setShowCreateInventoryModal(false);
                setCreateWithdrawQuantity(1);
                setInventoryFormData({
                  name: '',
                  description: '',
                  category: '',
                  quantity: 1,
                  unit: 'Stück',
                  locationId: '',
                  purchasePrice: 0,
                  sellingPrice: 0,
                  currentValue: 0,
                  partNumber: ''
                });
              } catch (error) {
                console.error('Error creating inventory item:', error);
                showNotification('Fehler beim Erstellen des Inventar-Items: ' + (error.response?.data?.error || error.message || 'Unbekannter Fehler', 'error'));
              }
            }}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={inventoryFormData.name}
                  onChange={(e) => setInventoryFormData({ ...inventoryFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  value={inventoryFormData.description}
                  onChange={(e) => setInventoryFormData({ ...inventoryFormData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Teilenummer</label>
                  <input
                    type="text"
                    value={inventoryFormData.partNumber}
                    onChange={(e) => setInventoryFormData({ ...inventoryFormData, partNumber: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Kategorie</label>
                  <input
                    type="text"
                    value={inventoryFormData.category}
                    onChange={(e) => setInventoryFormData({ ...inventoryFormData, category: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Werkstattort</label>
                <select
                  value={inventoryFormData.locationId}
                  onChange={(e) => setInventoryFormData({ ...inventoryFormData, locationId: e.target.value })}
                >
                  <option value="">Kein Ort</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Menge</label>
                  <input
                    type="number"
                    step="0.01"
                    value={inventoryFormData.quantity}
                    onChange={(e) => setInventoryFormData({ ...inventoryFormData, quantity: e.target.value === '' ? '' : e.target.value })}
                    onBlur={(e) => {
                      const val = e.target.value === '' ? 1 : parseFloat(e.target.value) || 1;
                      setInventoryFormData({ ...inventoryFormData, quantity: val });
                    }}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Einheit</label>
                  <input
                    type="text"
                    value={inventoryFormData.unit}
                    onChange={(e) => setInventoryFormData({ ...inventoryFormData, unit: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Einkaufspreis ({getCurrency()})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={inventoryFormData.purchasePrice}
                    onChange={(e) => setInventoryFormData({ ...inventoryFormData, purchasePrice: e.target.value === '' ? '' : e.target.value })}
                    onBlur={(e) => {
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                      setInventoryFormData({ ...inventoryFormData, purchasePrice: val });
                    }}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Verkaufspreis ({getCurrency()}) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={inventoryFormData.sellingPrice}
                    onChange={(e) => setInventoryFormData({ ...inventoryFormData, sellingPrice: e.target.value === '' ? '' : e.target.value })}
                    onBlur={(e) => {
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                      setInventoryFormData({ ...inventoryFormData, sellingPrice: val });
                    }}
                    min="0"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Menge entnehmen (max. {inventoryFormData.quantity || 0} {inventoryFormData.unit})</label>
                <input
                  type="number"
                  step="1"
                  value={createWithdrawQuantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    const max = inventoryFormData.quantity || 0;
                    setCreateWithdrawQuantity(Math.min(Math.max(0, val), max));
                  }}
                  min="0"
                  max={inventoryFormData.quantity || 0}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Erstellen und anwenden
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateInventoryModal(false);
                    setCreateWithdrawQuantity(1);
                    setInventoryFormData({
                      name: '',
                      description: '',
                      category: '',
                      quantity: 1,
                      unit: 'Stück',
                      locationId: '',
                      purchasePrice: 0,
                      sellingPrice: 0,
                      currentValue: 0,
                      partNumber: ''
                    });
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditRepair;
